// Pure helpers for email sign-in, shared by the app (instant form feedback)
// and the Worker (the checks that count). Unit tested.

export const CODE_LENGTH = 6;

// RFC 5322 "dot-atom" local part and a plain DNS domain, ASCII only. Strict on
// purpose: anything accepted here is deliverable and can never smuggle a
// header or a second address into the email.
const ATEXT = "[a-z0-9!#$%&'*+/=?^_`{|}~-]";
const EMAIL_RE = new RegExp(`^${ATEXT}+(?:\\.${ATEXT}+)*@(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\\.)+[a-z]{2,63}$`);

// Trimmed and lower-cased, or null if it is not a usable address.
export function normalizeEmail(input) {
  if (typeof input !== 'string') return null;
  const email = input.trim().toLowerCase();
  if (email.length > 254) return null;
  const at = email.lastIndexOf('@');
  if (at < 1 || at > 64) return null;
  return EMAIL_RE.test(email) ? email : null;
}

// A uniformly random six-digit code. 4,294,000,000 is a multiple of 10^6, so
// discarding draws at or above it removes any modulo bias.
export function newCode() {
  const buf = new Uint32Array(1);
  for (;;) {
    crypto.getRandomValues(buf);
    if (buf[0] < 4_294_000_000) return String(buf[0] % 1_000_000).padStart(CODE_LENGTH, '0');
  }
}

export function isCode(value) {
  return typeof value === 'string' && /^\d{6}$/.test(value);
}

// What the code box keeps from whatever was typed or pasted: digits only, at
// most six. A pasted sentence ("Your code is 482 913. It expires in 10
// minutes.") yields the code itself, not the first six digits in sight.
export function cleanCode(input) {
  const s = String(input ?? '');
  if (/[a-z]/i.test(s)) {
    const m = s.match(/(?:^|\D)(\d{3})[\s-]?(\d{3})(?!\d)/);
    if (m) return m[1] + m[2];
  }
  return s.replace(/\D/g, '').slice(0, CODE_LENGTH);
}

// Where to go after signing in. Only a path on this site is allowed, so a
// crafted link can never bounce someone to another domain.
export function safeNext(next, fallback = '/') {
  if (typeof next !== 'string' || !next.startsWith('/') || next.startsWith('//') || next.includes('\\')) return fallback;
  if (/^\/(signin|welcome)(\/|\?|$)/.test(next)) return fallback;
  return next;
}
